// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 19-design-system.md
// The unified Integrations tab: the org's outbound integrations (registry +
// credentials) and the grants apps and people hold against them, two halves of
// one story under one route (docs/28 §/integrations). A segmented control
// switches between the Integrations sub-view (the registry) and the Grants
// sub-view (the cross-app review inventory). The old
// /permissions route redirects here with ?view=grants.
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';

import { usePageTitle } from '@/components/app-shell/AppShell';
import { EmptyState } from '@/components/greenlight/EmptyState';
import { PageHeader } from '@/components/greenlight/PageHeader';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { IntegrationsView } from '@/components/integrations/IntegrationsView';
import { GrantsView } from '@/components/permissions/GrantsView';
import { api } from '@/lib/api';
import { isTransientQueryError, liveQueryOptions } from '@/lib/polling';
import { useAuth, useCurrentUser } from '@/lib/auth';
import type { PermissionListItem } from '@greenlight/shared';

type View = 'integrations' | 'grants';

// The contract's ceiling on a single page (docs/16 §Pagination) — the badge
// counts one page of pending rows rather than walking the whole queue.
const PENDING_BADGE_LIMIT = 100;

export function IntegrationsPage(): React.JSX.Element {
  usePageTitle('Integrations');
  const user = useCurrentUser();

  if (user?.role !== 'org_admin' && user?.role !== 'admin') {
    return (
      <div className="space-y-6" data-testid="integrations-page">
        <PageHeader eyebrow="Data brokering" title="Integrations" />
        <EmptyState
          title="Access restricted"
          description="Integrations and grants are available to IT administrators."
        />
      </div>
    );
  }

  return <UnifiedIntegrationsView userRole={user.role} />;
}

function UnifiedIntegrationsView({ userRole }: { userRole: string }): React.JSX.Element {
  const { user, refresh } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [registerOpen, setRegisterOpen] = React.useState(false);

  // The URL owns the active sub-view so it stays deep-linkable and shareable
  // (docs/28 §Cross-cutting — deep-linking); anything but the explicit `grants`
  // value resolves to the registry.
  const view: View = searchParams.get('view') === 'grants' ? 'grants' : 'integrations';

  function setView(next: View): void {
    const p = new URLSearchParams(searchParams);
    if (next === 'grants') p.set('view', 'grants');
    else p.delete('view');
    setSearchParams(p, { replace: true });
  }

  // Drives the pending badge on the Grants sub-tab so the cross-app review queue
  // stays first-class even from the registry. Counts server-side-filtered pending
  // rows at the largest page the contract allows, so the badge reflects the queue
  // rather than whatever fits in a default page; beyond that it reads "100+"
  // rather than a wrong exact number. Withheld (undefined) while loading or
  // access is denied so the badge never shows a misleading zero.
  const badgeKey = ['permissions-queue', 'pending-badge'];
  const queueQuery = api.permissions.queue.useQuery(
    badgeKey,
    { query: { status: 'pending', limit: PENDING_BADGE_LIMIT } },
    { ...liveQueryOptions, queryKey: badgeKey },
  );
  const pendingPage =
    !queueQuery.isLoading &&
    (!queueQuery.isError || isTransientQueryError(queueQuery.error)) &&
    queueQuery.data?.status === 200
      ? queueQuery.data.body
      : undefined;
  const pendingGrants = pendingPage?.items.filter(
    (p: PermissionListItem) => p.status === 'pending',
  ).length;
  const pendingCapped = pendingPage?.next_cursor != null;

  const tabItems: { value: View; label: React.ReactNode; testId: string }[] = [
    {
      value: 'integrations',
      label: 'Integrations',
      testId: 'view-tab-integrations',
    },
    {
      value: 'grants',
      testId: 'view-tab-grants',
      label:
        pendingGrants !== undefined && pendingGrants > 0 ? (
          <>
            Grants
            <span
              className="ml-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-[hsl(var(--status-amber)/0.18)] px-1 font-mono text-[10px] text-[hsl(var(--status-amber-text))]"
              aria-label={
                pendingCapped
                  ? `More than ${pendingGrants} pending grants`
                  : `${pendingGrants} pending grants`
              }
            >
              {pendingCapped ? `${pendingGrants}+` : pendingGrants}
            </span>
          </>
        ) : (
          'Grants'
        ),
    },
  ];

  return (
    <div className="space-y-6" data-testid="integrations-page">
      <PageHeader
        eyebrow="Data brokering"
        title="Integrations"
        actions={
          view === 'integrations' ? (
            <Button onClick={() => setRegisterOpen(true)}>+ Register integration</Button>
          ) : undefined
        }
      />

      <Tabs value={view} onValueChange={(v) => setView(v as View)} activationMode="manual">
        <TabsList
          aria-label="Integrations and grants"
          className="flex h-auto w-full justify-start gap-2 overflow-x-auto rounded-none border-b border-border bg-transparent p-0 text-foreground [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {tabItems.map((t) => (
            <TabsTrigger
              key={t.value}
              value={t.value}
              data-testid={t.testId}
              className="-mb-px h-12 shrink-0 rounded-none border-b-[3px] border-transparent px-3 text-[15px] font-medium text-foreground hover:border-border data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:font-bold data-[state=active]:shadow-none data-[state=active]:ring-0"
            >
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="integrations" className="mt-6">
          <IntegrationsView
            userRole={userRole}
            registerOpen={registerOpen}
            onRegisterOpenChange={setRegisterOpen}
            pendingGrants={pendingPage?.items}
            requireDataAccessTerms={user?.data_access_terms_accepted === false}
            onDataAccessTermsAccepted={() => void refresh()}
          />
        </TabsContent>
        <TabsContent value="grants" className="mt-6">
          <GrantsView />
        </TabsContent>
      </Tabs>
    </div>
  );
}
